import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  RefreshCw, Eye, Pencil, Save, X, CheckCircle2, Trash2, Send, Rocket, Loader2,
} from "lucide-react";
import toast from "react-hot-toast";

// Actionable native review queue: lists the CRM-drafted content package's pages
// and lets a super-admin review, edit, approve (import to WordPress as a draft),
// set live, and delete — all from the CRM, using the SSO-obtained AI-SEO token.
// No iframe, no bouncing to the full app.
const AISEO_API = (process.env.REACT_APP_AISEO_API_URL || "https://collings-ai.onrender.com/v1").replace(/\/+$/, "");

const STATUS = {
  pending:  { label: "Draft",    cls: "bg-amber-100 text-amber-700" },
  importing:{ label: "Importing",cls: "bg-blue-100 text-blue-700" },
  imported: { label: "On WP (draft)", cls: "bg-blue-100 text-blue-700" },
  failed:   { label: "Failed",   cls: "bg-red-100 text-red-700" },
  skipped:  { label: "Skipped",  cls: "bg-gray-100 text-gray-500" },
  deleted:  { label: "Deleted",  cls: "bg-gray-100 text-gray-500" },
};

const AiSeoReviewQueue = ({ token, siteId }) => {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [pkg, setPkg] = useState(null);       // { _id, title, pages:[...] }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(null);      // expanded pageIndex
  const [editing, setEditing] = useState(null);// pageIndex being edited
  const [draft, setDraft] = useState({ title: "", content: "" });
  const [busy, setBusy] = useState(null);      // `${idx}:${action}` in-flight

  const load = useCallback(async () => {
    if (!token || !siteId) return;
    setLoading(true); setError("");
    try {
      const list = await axios.get(`${AISEO_API}/content-packages/${siteId}`, auth);
      const pkgs = (list.data?.packages || list.data || []).filter((p) => p.source === "crm_event");
      if (!pkgs.length) { setPkg(null); return; }
      // Newest CRM-event package (the rolling "Property Events" one).
      const latest = pkgs.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
      const detail = await axios.get(`${AISEO_API}/content-packages/${siteId}/${latest._id || latest.id}`, auth);
      setPkg(detail.data?.package || detail.data);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || "Couldn't load the review queue.");
    } finally {
      setLoading(false);
    }
  }, [token, siteId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const pkgId = pkg && (pkg._id || pkg.id);
  const act = async (key, fn, okMsg) => {
    setBusy(key);
    try { await fn(); if (okMsg) toast.success(okMsg); await load(); }
    catch (e) { toast.error(e?.response?.data?.error || "Action failed."); }
    finally { setBusy(null); }
  };

  const base = `${AISEO_API}/content-packages/${siteId}/${pkgId}`;
  const markReviewed = (i) => act(`${i}:review`, () => axios.post(`${base}/pages/${i}/review`, {}, auth), "Marked reviewed");
  const approve = (i) => act(`${i}:import`, () => axios.post(`${base}/import`, { pageIndexes: [i] }, auth), "Sent to WordPress as a draft");
  const setLive = (i) => act(`${i}:publish`, () => axios.post(`${base}/pages/${i}/publish`, {}, auth), "Published live");
  const remove = (i) => { if (window.confirm("Delete this draft?")) act(`${i}:delete`, () => axios.delete(`${base}/pages/${i}`, auth), "Deleted"); };
  const saveEdit = (i) => act(`${i}:save`, async () => {
    await axios.patch(`${base}/pages/${i}`, { title: draft.title, content: draft.content }, auth);
    setEditing(null);
  }, "Saved");

  const startEdit = (i, page) => { setEditing(i); setOpen(i); setDraft({ title: page.title || "", content: page.content || "" }); };

  if (loading) return <div className="flex items-center gap-2 text-gray-500 text-sm p-4"><Loader2 className="w-4 h-4 animate-spin" /> Loading review queue…</div>;
  if (error) return <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-sm px-3 py-2">{error}</div>;
  const pages = (pkg?.pages || []).filter((p) => p.importStatus !== "deleted");

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500">Review queue{pkg ? ` · ${pkg.title}` : ""}</h2>
        <button onClick={load} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
      </div>

      {pages.length === 0 ? (
        <div className="text-sm text-gray-400 py-8 text-center">No drafts waiting. New sales, listings, or agent requests will appear here for review.</div>
      ) : (
        <div className="divide-y divide-gray-100">
          {pages.map((page, i) => {
            // "Set live" flips the WP post to publish (page.wpStatus/desiredStatus
            // = 'publish') but leaves importStatus at 'imported' — so key the
            // "Live" state off wpStatus, not importStatus, or the badge/button
            // never change after publishing.
            const isLive = page.wpStatus === "publish" || page.desiredStatus === "publish";
            const st = isLive
              ? { label: "Live on site", cls: "bg-emerald-100 text-emerald-700" }
              : (STATUS[page.importStatus] || STATUS.pending);
            const isPending = page.importStatus === "pending" || !page.importStatus;
            const isImported = page.importStatus === "imported" && !isLive;
            const b = (a) => busy === `${i}:${a}`;
            return (
              <div key={i} className="py-3">
                <div className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 truncate">{page.title || "(untitled draft)"}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${st.cls}`}>{st.label}</span>
                      {page.reviewedAt && <span className="text-[11px] text-emerald-600 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> reviewed</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button title="Preview" onClick={() => { setOpen(open === i ? null : i); setEditing(null); }} className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100"><Eye className="w-4 h-4" /></button>
                    {isPending && <button title="Edit" onClick={() => startEdit(i, page)} className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100"><Pencil className="w-4 h-4" /></button>}
                    {!page.reviewedAt && <button title="Mark reviewed" disabled={b("review")} onClick={() => markReviewed(i)} className="p-1.5 rounded-lg text-emerald-600 hover:bg-emerald-50 disabled:opacity-40">{b("review") ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}</button>}
                    {isPending && <button title="Approve → send to WordPress (draft)" disabled={b("import")} onClick={() => approve(i)} className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50">{b("import") ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Approve</button>}
                    {isImported && <button title="Set live on the website" disabled={b("publish")} onClick={() => setLive(i)} className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">{b("publish") ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Rocket className="w-3.5 h-3.5" />} Set live</button>}
                    <button title="Delete" disabled={b("delete")} onClick={() => remove(i)} className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 disabled:opacity-40"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>

                {open === i && (
                  <div className="mt-3 rounded-lg border border-gray-100 bg-gray-50 p-3">
                    {editing === i ? (
                      <div className="space-y-2">
                        <input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200" placeholder="Title" />
                        <textarea value={draft.content} onChange={(e) => setDraft({ ...draft, content: e.target.value })} rows={10} className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 font-mono" placeholder="Content (HTML)" />
                        <div className="flex gap-2">
                          <button disabled={b("save")} onClick={() => saveEdit(i)} className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg bg-gray-900 text-white hover:bg-black disabled:opacity-50">{b("save") ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save</button>
                          <button onClick={() => setEditing(null)} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100"><X className="w-4 h-4" /> Cancel</button>
                        </div>
                      </div>
                    ) : (
                      // Preview in a locked-down sandboxed iframe (no allow-scripts),
                      // so draft HTML renders but can never execute script — no XSS.
                      <iframe
                        title={`preview-${i}`}
                        sandbox=""
                        srcDoc={page.content || "<p style='color:#9ca3af;font-family:sans-serif'>No content.</p>"}
                        className="w-full h-72 rounded border border-gray-200 bg-white"
                      />
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AiSeoReviewQueue;
